/**
 * ResetPasswordPage — standalone route at /reset-password?token=...
 *
 * State machine (design-migration-completion §16.5):
 *
 *   Mount:
 *     - read `token` from URLSearchParams
 *     - if absent → render "missing token" error and DO NOT show the form
 *
 *   Form:
 *     - two <input type="password"> with show/hide
 *     - submit button enabled iff both non-empty AND match exactly AND length ≥ 8
 *
 *   Submit:
 *     - POST /auth/password-reset/redeem
 *     - 200: clear local credentials, show "redirecting…", navigate to /signin
 *       after 2s
 *     - 400 token_invalid_or_expired: render the warm "expired or used" message
 *     - 429: render "too many attempts" message
 *
 * Validates: Requirements 25.1, 25.3, 25.4, 25.6, 25.7, 26.2.
 */
import { useEffect, useState } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { PanelSurface } from "../chrome/PanelSurface";
import { PillButton } from "../chrome/PillButton";
import { auth as authApi } from "../api";

const MIN_PASSWORD_LEN = 8;

type Phase = "form" | "submitting" | "success" | "error";

interface ResetPasswordPageProps {
  /** Override token resolution for tests. Defaults to URLSearchParams.get('token'). */
  initialToken?: string | null;
  /** Override redirect destination for tests. Defaults to "/signin". */
  redirectTo?: string;
  /** Disable the auto-redirect after success. Used by tests. */
  disableRedirect?: boolean;
}

export function ResetPasswordPage({
  initialToken,
  redirectTo = "/signin",
  disableRedirect,
}: ResetPasswordPageProps = {}) {
  const [token, setToken] = useState<string | null>(() => {
    // Resolve token synchronously so the first render reflects the real
    // state (token present → form, token absent → error message). Falling
    // back to a useEffect would briefly flash the error shell while the
    // effect resolves, which breaks tests that assert the initial DOM.
    if (initialToken !== undefined) return initialToken;
    try {
      return new URLSearchParams(window.location.search).get("token");
    } catch {
      return null;
    }
  });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [phase, setPhase] = useState<Phase>("form");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Allow tests to swap the token mid-test by changing initialToken.
  useEffect(() => {
    if (initialToken !== undefined) setToken(initialToken);
  }, [initialToken]);

  // Auto-redirect after success (Req 25.7).
  useEffect(() => {
    if (phase !== "success" || disableRedirect) return;
    const handle = setTimeout(() => {
      try {
        window.location.assign(redirectTo);
      } catch {
        /* SSR / test */
      }
    }, 2000);
    return () => clearTimeout(handle);
  }, [phase, redirectTo, disableRedirect]);

  // Token missing — short-circuit (Req 25.6).
  if (token === null) {
    return (
      <ResetShell title="Reset password">
        <p
          style={{
            color: ChromeTokens.color.ink,
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          This link has expired or already been used. Sign in to send a new
          one — or pick &quot;Forgot password&quot; if you don&apos;t remember
          it.
        </p>
        <div style={{ marginTop: ChromeTokens.spacing.md }}>
          <PillButton
            size="md"
            tone="primary"
            ariaLabel="Sign in"
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.assign("/?view=login");
              }
            }}
          >
            Sign in
          </PillButton>
        </div>
      </ResetShell>
    );
  }

  if (phase === "success") {
    return (
      <ResetShell title="Password updated">
        <p
          role="status"
          style={{
            color: ChromeTokens.color.ink,
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          Password updated! Redirecting to sign in…
        </p>
      </ResetShell>
    );
  }

  const matches = pw1 === pw2;
  const longEnough = pw1.length >= MIN_PASSWORD_LEN;
  const canSubmit = matches && longEnough && pw1.length > 0 && phase === "form";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase("submitting");
    setErrorMessage("");
    try {
      await authApi.redeemPasswordReset(token, pw1);
      // Clear local credentials so the SignIn page starts fresh (Req 26.2).
      try {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("child_session_token");
        sessionStorage.removeItem("is_child_token");
      } catch {
        /* ignore */
      }
      setPhase("success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("rate_limit_exceeded")) {
        setErrorMessage("Too many attempts. Try again in 5 minutes.");
      } else if (msg.includes("token_invalid_or_expired")) {
        setErrorMessage(
          "This link has expired or already been used. Sign in to send a new one — or pick \"Forgot password\" if you don't remember it.",
        );
      } else {
        setErrorMessage("Couldn't update your password. Try again.");
      }
      setPhase("error");
    }
  };

  return (
    <ResetShell title="Set a new password">
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: ChromeTokens.spacing.md,
          fontFamily: ChromeTokens.font.ui,
        }}
      >
        <PasswordRow
          label="New password"
          value={pw1}
          onChange={setPw1}
          showText={showPw}
          onToggleShow={() => setShowPw((v) => !v)}
        />
        <PasswordRow
          label="Confirm new password"
          value={pw2}
          onChange={setPw2}
          showText={showPw}
          onToggleShow={() => setShowPw((v) => !v)}
        />
        {pw1.length > 0 && !longEnough && (
          <p
            style={{
              fontSize: 12,
              color: ChromeTokens.color.inkMuted,
              margin: 0,
            }}
          >
            Use at least {MIN_PASSWORD_LEN} characters.
          </p>
        )}
        {pw2.length > 0 && !matches && (
          <p
            style={{
              fontSize: 12,
              color: ChromeTokens.color.danger,
              margin: 0,
            }}
          >
            The two passwords don't match.
          </p>
        )}
        {errorMessage && phase === "error" && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: ChromeTokens.color.danger,
              margin: 0,
            }}
          >
            {errorMessage}
          </p>
        )}
        <div>
          <PillButton
            size="md"
            tone="primary"
            ariaLabel="Set password"
            disabled={!canSubmit}
            onClick={() => {
              /* form submit handles it */
            }}
          >
            {phase === "submitting" ? "Saving…" : "Set password"}
          </PillButton>
        </div>
      </form>
    </ResetShell>
  );
}

function ResetShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <PanelSurface
      open={true}
      onDismiss={() => {
        /* full-page surface — no dismiss */
      }}
      ariaLabel={title}
      anchor="center"
      dismissOnOutsideClick={false}
    >
      <div
        style={{
          width: "min(420px, 92vw)",
          display: "flex",
          flexDirection: "column",
          gap: ChromeTokens.spacing.md,
          fontFamily: ChromeTokens.font.ui,
        }}
      >
        <h1
          style={{
            margin: 0,
            color: ChromeTokens.color.ink,
            fontSize: 24,
            fontFamily: ChromeTokens.font.display,
          }}
        >
          {title}
        </h1>
        {children}
      </div>
    </PanelSurface>
  );
}

function PasswordRow({
  label,
  value,
  onChange,
  showText,
  onToggleShow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  showText: boolean;
  onToggleShow: () => void;
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: ChromeTokens.color.ink,
          marginBottom: ChromeTokens.spacing.xs,
        }}
      >
        {label}
      </label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ChromeTokens.spacing.xs,
          padding: `${ChromeTokens.spacing.xs}px ${ChromeTokens.spacing.sm}px`,
          border: `1.5px solid ${ChromeTokens.color.baseMuted}`,
          borderRadius: ChromeTokens.radius.md,
          background: ChromeTokens.color.base,
        }}
      >
        <input
          aria-label={label}
          type={showText ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            color: ChromeTokens.color.ink,
            fontFamily: "inherit",
            fontSize: 15,
          }}
        />
        <button
          type="button"
          aria-label={showText ? "Hide password" : "Show password"}
          onClick={onToggleShow}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: ChromeTokens.spacing.xs,
            color: ChromeTokens.color.inkMuted,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <Icon name={showText ? "eye-off" : "eye"} size={16} />
        </button>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
